import React from "react";
import T from "../../../components/common/T";

const PROJECT_STAGES = [
  "Draft",
  "Confirmed",
  "Internal Approved",
  "Govt. Approved",
  "In Progress",
  "Ready to Possession",
  "Ready to Move",
  "Canceled"
];

const ProjectStageNavigator = ({ currentStatus, viewingStage, setViewingStage }) => {
  const realStatusIndex = PROJECT_STAGES.indexOf(currentStatus);
  const activeIndex = realStatusIndex >= 0 ? realStatusIndex : 0;
  const isCanceledActive = currentStatus === "Canceled";

  return (
    <div className="w-full bg-white dark:bg-[#161b22] transition-colors">
      {/* ── TOP METADATA BAR: Live Status + Viewing State + Progress ── */}
      <div className="px-4 py-2.5 flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 dark:border-[#21262d] text-xs">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-[#848d97]">
            <T>Project Lifecycle</T>
          </span>
          <span className="text-gray-300 dark:text-[#30363d]">/</span>
          
          {/* Active Status Badge */}
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full font-bold text-[11px] transition ${
              isCanceledActive
                ? "bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/50"
                : currentStatus === "Ready to Move"
                ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/50"
                : "bg-orange-50 dark:bg-orange-950/40 text-[#D9531E] dark:text-[#F5875E] border border-orange-200/80 dark:border-orange-800/60"
            }`}
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-current"></span>
            </span>
            <T>Current</T>: {currentStatus}
          </span>

          {/* Viewing Badge (shown when inspecting another stage) */}
          {viewingStage && viewingStage !== currentStatus && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800/50 animate-fade-in">
              <T>Viewing</T>: <strong className="font-bold">{viewingStage}</strong>
              <button
                type="button"
                onClick={() => setViewingStage(currentStatus)}
                className="ml-1 underline font-bold hover:text-amber-900 dark:hover:text-white transition"
                title="Return to active stage"
              >
                (<T>Back to Active</T>)
              </button>
            </span>
          )}
        </div>

        {/* Stage Counter Progress */}
        <div className="flex items-center gap-2.5 text-gray-500 dark:text-[#848d97] text-[11px]">
          <span>
            <T>Stage</T> <strong className="text-gray-900 dark:text-[#f0f6fc]">{activeIndex + 1}</strong> of {PROJECT_STAGES.length}
          </span>
          <div className="w-16 h-1.5 bg-gray-200 dark:bg-[#30363d] rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${isCanceledActive ? "bg-red-500" : "bg-[#D9531E]"}`}
              style={{ width: `${Math.round(((activeIndex + 1) / PROJECT_STAGES.length) * 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* ── VECTOR CHEVRON PIPELINE BAR ── */}
      <div className="flex w-full overflow-x-auto scrollbar-none h-11 bg-gray-100/70 dark:bg-[#0d1117] border-b border-gray-200 dark:border-[#30363d] select-none">
        {PROJECT_STAGES.map((stage, index) => {
          const isLast = index === PROJECT_STAGES.length - 1;
          const isRealStatus = stage === currentStatus;
          const isViewing = stage === viewingStage;
          const isPast = index < realStatusIndex && !isCanceledActive;
          const isCanceledStage = stage === "Canceled";

          // Dynamic colors for stage button and matching SVG chevron arrow
          let bgClass = "bg-white dark:bg-[#161b22] text-gray-500 dark:text-[#6e7681] hover:bg-gray-50 dark:hover:bg-[#21262d] hover:text-gray-800 dark:hover:text-[#848d97]";
          let arrowFill = "fill-white dark:fill-[#161b22]";

          if (isRealStatus) {
            if (isCanceledStage) {
              bgClass = "bg-red-600 dark:bg-red-600 text-white font-bold shadow-xs";
              arrowFill = "fill-red-600 dark:fill-red-600";
            } else {
              bgClass = "bg-[#D9531E] dark:bg-[#D9531E] text-white font-bold shadow-xs";
              arrowFill = "fill-[#D9531E] dark:fill-[#D9531E]";
            }
          } else if (isViewing) {
            bgClass = "bg-gray-100 dark:bg-[#21262d] text-gray-900 dark:text-[#f0f6fc] font-bold ring-2 ring-inset ring-orange-500/70 dark:ring-orange-400/80";
            arrowFill = "fill-gray-100 dark:fill-[#21262d]";
          } else if (isPast) {
            bgClass = "bg-gray-50 dark:bg-[#1a212d] text-gray-700 dark:text-[#848d97] hover:bg-gray-100 dark:hover:bg-[#21262d] hover:text-gray-900 dark:hover:text-[#f0f6fc]";
            arrowFill = "fill-gray-50 dark:fill-[#1a212d]";
          } else if (isCanceledStage) {
            bgClass = "bg-white dark:bg-[#161b22] text-red-500/70 hover:text-red-600 hover:bg-red-50/50 dark:hover:bg-red-950/20";
            arrowFill = "fill-white dark:fill-[#161b22]";
          }

          return (
            <button
              key={stage}
              type="button"
              onClick={() => setViewingStage(stage)}
              style={{ zIndex: 30 - index }}
              className={`
                relative flex-1 min-w-[110px] md:min-w-0 h-full flex items-center justify-center
                transition-colors duration-150 focus:outline-none
                ${index !== 0 ? "pl-3.5 sm:pl-4.5" : "pl-2 sm:pl-3"}
                pr-2 sm:pr-3
                ${bgClass}
              `}
              title={`${stage} ${isRealStatus ? "(Active Stage)" : isPast ? "(Completed)" : ""}`}
            >
              <div className="flex items-center justify-center gap-1.5 w-full">
                {/* State Indicator Icon */}
                {isPast ? (
                  <span className="w-3.5 h-3.5 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center text-[9px] font-bold shrink-0">
                    ✓
                  </span>
                ) : isRealStatus ? (
                  <span className="w-2 h-2 rounded-full bg-white shrink-0 animate-pulse" />
                ) : isCanceledStage ? (
                  <span className="w-3 h-3 rounded-full bg-red-500/20 text-red-500 flex items-center justify-center text-[8px] font-bold shrink-0">
                    ✕
                  </span>
                ) : (
                  <span className="w-3.5 h-3.5 rounded-full bg-gray-200/80 dark:bg-[#21262d] text-gray-500 dark:text-[#6e7681] flex items-center justify-center text-[9px] font-mono shrink-0">
                    {index + 1}
                  </span>
                )}

                {/* Stage Text */}
                <span className="text-[10px] sm:text-[11px] leading-tight text-center truncate font-semibold tracking-tight">
                  <T>{stage}</T>
                </span>
              </div>

              {/* Vector SVG Arrow Tip (Overlaps the next stage cleanly, zero white borders) */}
              {!isLast && (
                <div className="absolute top-0 -right-3 h-full w-3 pointer-events-none z-10">
                  <svg viewBox="0 0 12 44" preserveAspectRatio="none" className="h-full w-full">
                    <polygon points="0,0 12,22 0,44" className={arrowFill} />
                    <polyline
                      points="0,0 12,22 0,44"
                      fill="none"
                      className="stroke-gray-300 dark:stroke-[#30363d]"
                      strokeWidth="1.5"
                    />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ProjectStageNavigator;